import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Trash, House } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import { toast } from "sonner";

// Modal handles BOTH create and edit (when `initial` is null → create mode).
// Fields: name, description, parent, cover_image (uploaded via /api/upload).
export default function EditLocationModal({ open, onClose, onSaved, initial = null, allLocations = [] }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [parentId, setParentId] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name || "");
      setDescription(initial.description || "");
      setParentId(initial.parent_id || "");
      setCoverImage(initial.cover_image || "");
    } else {
      setName(""); setDescription(""); setParentId(""); setCoverImage("");
    }
  }, [open, initial]);

  // Exclude self & descendants from parent dropdown (would create a cycle)
  const validParents = (() => {
    if (!initial) return allLocations;
    const banned = new Set([initial.id]);
    let added = true;
    while (added) {
      added = false;
      for (const l of allLocations) {
        if (l.parent_id && banned.has(l.parent_id) && !banned.has(l.id)) {
          banned.add(l.id); added = true;
        }
      }
    }
    return allLocations.filter((l) => !banned.has(l.id));
  })();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverImage(data.path);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        description: description || "",
        parent_id: parentId || null,
        cover_image: coverImage || "",
      };
      if (initial) {
        await api.patch(`/locations/${initial.id}`, body);
        toast.success("Location updated");
      } else {
        await api.post("/locations", body);
        toast.success("Location added");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-stoneink-900/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={onClose}
          data-testid="edit-location-modal"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-3xl bg-white shadow-hover overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-sand-200 sticky top-0 bg-white z-10">
              <h2 className="font-heading text-2xl text-stoneink-900 font-semibold">
                {initial ? "Edit location" : "Add a location"}
              </h2>
              <button onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center hover:bg-sand-200" data-testid="edit-location-close">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-5">
              {/* Cover image */}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Cover image</label>
                <div className="mt-2 rounded-2xl border border-dashed border-sand-300 bg-sand-100 overflow-hidden aspect-[16/9] relative" data-testid="edit-location-cover-zone">
                  {coverImage ? (
                    <>
                      <AuthedImage path={coverImage} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setCoverImage("")}
                        className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-stoneink-900/70 text-white hover:bg-stoneink-900"
                        data-testid="edit-location-cover-remove"
                        aria-label="Remove cover image"
                      >
                        <Trash size={14} />
                      </button>
                    </>
                  ) : (
                    <label className="absolute inset-0 cursor-pointer flex flex-col items-center justify-center text-stoneink-500 hover:bg-sand-200 transition-colors" data-testid="edit-location-cover-upload">
                      <Camera size={28} weight="duotone" />
                      <span className="mt-2 text-sm font-medium">{uploading ? "Uploading…" : "Click to add a cover photo"}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Name</label>
                <input
                  data-testid="edit-location-name"
                  value={name} onChange={(e) => setName(e.target.value)} required
                  placeholder="e.g. Hall Closet"
                  className="mt-2 w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Notes</label>
                <input
                  data-testid="edit-location-description"
                  value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional — what lives here?"
                  className="mt-2 w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Inside</label>
                <select
                  data-testid="edit-location-parent"
                  value={parentId} onChange={(e) => setParentId(e.target.value)}
                  className="mt-2 w-full h-11 px-3 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
                >
                  <option value="">— Top level (no parent) —</option>
                  {validParents.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <p className="text-xs text-stoneink-500 mt-1.5 flex items-center gap-1">
                  <House size={12} weight="duotone" /> Nest this inside another location, like &ldquo;Top Shelf&rdquo; inside &ldquo;Hall Closet&rdquo;.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-sand-200">
                <button type="button" onClick={onClose} className="h-11 px-5 rounded-xl text-stoneink-700 hover:bg-sand-200 font-medium" data-testid="edit-location-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={busy || uploading} className="h-11 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 disabled:opacity-50 text-white font-semibold" data-testid="edit-location-save">
                  {busy ? "Saving…" : (initial ? "Save changes" : "Add location")}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
